import { Suspense, lazy } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { PasswordGate } from "@/components/PasswordGate";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { AgeVerificationProvider } from "@/contexts/AgeVerificationContext";
import { GiftProvider } from "@/contexts/GiftContext";

// Eagerly loaded pages (critical path)
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Loading fallback
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Lazy loaded pages - grouped by priority
// High priority (likely to be visited)
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const PartnerProfile = lazy(() => import("./pages/PartnerProfile"));
const CartPage = lazy(() => import("./pages/CartPage"));
const CartCheckout = lazy(() => import("./pages/CartCheckout"));

// Medium priority
const InterpretiPage = lazy(() => import("./pages/InterpretiPage"));
const VoucherDetail = lazy(() => import("./pages/VoucherDetail"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const BonusProductsPage = lazy(() => import("./pages/BonusProducts"));
const GiftsPage = lazy(() => import("./pages/GiftsPage"));

// Lower priority (specialized pages)
const SimpleProductDetail = lazy(() => import("./pages/SimpleProductDetail"));
const BundleDetail = lazy(() => import("./pages/BundleDetail"));
const PairProductDetail = lazy(() => import("./pages/PairProductDetail"));
const CustomBoxBuilder = lazy(() => import("./pages/CustomBoxBuilder"));
const PackageProductDetail = lazy(() => import("./pages/PackageProductDetail"));
const AdultProductDetail = lazy(() => import("./pages/AdultProductDetail"));
const VolumeProductDetail = lazy(() => import("./pages/VolumeProductDetail"));

// Info pages
const ShippingInfo = lazy(() => import("./pages/ShippingInfo"));
const TrackingPage = lazy(() => import("./pages/TrackingPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const GDPRPage = lazy(() => import("./pages/GDPRPage"));

// Internal/Dev pages
const EmailSignature = lazy(() => import("./pages/EmailSignature"));
const EmailOrderConfirmation = lazy(() => import("./pages/EmailOrderConfirmation"));
const Assets = lazy(() => import("./pages/Assets"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <CartProvider>
        <AgeVerificationProvider>
        <GiftProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <PasswordGate password="teamrukahore">
              <BrowserRouter>
                <ScrollToTop />
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Home - eagerly loaded */}
                    <Route path="/" element={<Index />} />
                    
                    {/* E-commerce core routes */}
                    <Route path="/kategoria/:categorySlug" element={<CategoryPage />} />
                    <Route path="/kategoria/:categorySlug/:subcategorySlug" element={<CategoryPage />} />
                    <Route path="/produkt/:id" element={<ProductDetail />} />
                    <Route path="/kosik" element={<CartPage />} />
                    <Route path="/pokladna" element={<CartCheckout />} />
                    
                    {/* Partner/Interpret routes */}
                    <Route path="/interpreti" element={<InterpretiPage />} />
                    <Route path="/partner/:slug" element={<PartnerProfile />} />
                    <Route path="/partner/:slug/:subcategorySlug" element={<PartnerProfile />} />
                    <Route path="/interpret/:slug" element={<PartnerProfile />} />
                    <Route path="/koza-bobkov" element={<PartnerProfile />} />
                    <Route path="/koza-bobkov/:subcategorySlug" element={<PartnerProfile />} />
                    
                    {/* Product variants */}
                    <Route path="/produkt-simple/:id" element={<SimpleProductDetail />} />
                    <Route path="/bundle/:id" element={<BundleDetail />} />
                    <Route path="/parovy-set/:id" element={<PairProductDetail />} />
                    <Route path="/vlastny-box/:id" element={<CustomBoxBuilder />} />
                    <Route path="/balenie/:id" element={<PackageProductDetail />} />
                    <Route path="/18-plus/:id" element={<AdultProductDetail />} />
                    <Route path="/mnozstvo/:id" element={<VolumeProductDetail />} />
                    <Route path="/poukazka" element={<VoucherDetail />} />
                    
                    {/* User */}
                    <Route path="/profil" element={<UserProfile />} />
                    <Route path="/bonus-produkty" element={<BonusProductsPage />} />
                    <Route path="/darceky" element={<GiftsPage />} />
                    
                    {/* Info pages */}
                    <Route path="/doprava" element={<ShippingInfo />} />
                    <Route path="/tracking" element={<TrackingPage />} />
                    <Route path="/kontakt" element={<ContactPage />} />
                    <Route path="/gdpr" element={<GDPRPage />} />
                    
                    {/* Internal/Dev pages */}
                    <Route path="/email-podpis" element={<EmailSignature />} />
                    <Route path="/email-objednavka" element={<EmailOrderConfirmation />} />
                    <Route path="/assety" element={<Assets />} />
                    
                    {/* 404 - eagerly loaded */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </BrowserRouter>
            </PasswordGate>
          </TooltipProvider>
        </GiftProvider>
        </AgeVerificationProvider>
      </CartProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
